// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, memo } from 'react';
import styled from '@emotion/styled';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';

import { useDashboardCalendarCheckList } from './hooks';
import {
  fontWeight,
  fontSize,
  spacing,
  mediaMax,
  borderRadius,
  zIndex,
} from 'presentations/views/styles';
import {
  black,
  colorPalette,
  gray02,
  gray03,
  gray04,
  white,
} from 'presentations/views/styles/color';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Modal from 'presentations/views/components/atoms/Modal';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Icon from 'presentations/views/components/atoms/Icon';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { convertHexToRGBA } from 'presentations/views/helpers/color';
import { ViewerItem } from 'presentations/api/schema/user';
import Select from 'presentations/views/components/atoms/Select';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { Role } from 'domains/models/team_user/role';
import AutoCompleteTeamUserField from '../../molecules/AutoCompleteTeamUserField';

export interface Props {
  isOpen: boolean;
  viewer: ViewerItem;
  isLoadingTeamUsers: boolean;
  viewerTeamUser?: TeamUserListItem;
  selectableTeamUsers: TeamUserListItem[];
  selectableUserUids: string[];
  onChangeUserSort: (sortedUsers: TeamUserListItem[]) => void;
  onChangeUserAdd: (newUser: TeamUserListItem) => void;
  onChangeUserDelete: Function;
  onClickInvite?: (e: SyntheticEvent<HTMLDivElement>) => void;
  onCloseFilter?: Function;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const { state, actions } = useDashboardCalendarCheckList(props);

  const isAdmin = props.viewer.role <= Role.Admin;

  return (
    <Modal
      isOpen={props.isOpen}
      onChange={state.isRequesting ? undefined : props.onCloseFilter}
    >
      <MobileFilterContainer>
        <ModalHeader>
          <InnerModalTitle>{t('Member Display Settings')}</InnerModalTitle>
          <ModalCloseIconButton
            onClick={props.onCloseFilter}
            isDisabled={state.isRequesting}
          >
            <Icon name="close" />
          </ModalCloseIconButton>
        </ModalHeader>
        <ModalContent>
          <SectionHeader>
            <SectionTitle>{t('Team Member')}</SectionTitle>
            {isAdmin && (
              <>
                <NaviIconButton onClick={props.onClickInvite}>
                  <PlusIcon name="plus" />
                </NaviIconButton>
                <PlusText onClick={props.onClickInvite}>{t('add')}</PlusText>
              </>
            )}
          </SectionHeader>
          <ProjectContainer>
            <AutoCompleteTeamUserField
              name={'teamUserOrder'}
              viewer={props.viewer}
              isExcludeViewer={true}
              selectedTeamUserIds={props.selectableTeamUsers.map(
                (u) => u.userId,
              )}
              onUpdate={actions.onSelectNewUser}
              isDisabled={false}
            />
          </ProjectContainer>
          {props.viewerTeamUser && (
            <CalendarListItemComponent>
              <CalendarName
                color={convertHexToRGBA(props.viewerTeamUser.color, 80)}
              >
                <ItemDragIcon name="" />
                <UserAvatar
                  src={props.viewerTeamUser.profileImage || ''}
                  alt={props.viewerTeamUser.name}
                />
                <CalendarNameText>{props.viewerTeamUser.name}</CalendarNameText>
              </CalendarName>
            </CalendarListItemComponent>
          )}
          <DragDropContext
            onDragStart={actions.onDragStart}
            onDragEnd={actions.onDragEnd}
          >
            <Droppable droppableId="UserList">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {state.teamUsers
                    .filter((u) => u.userId !== props.viewer.uid)
                    .map((teamUser, index) => {
                      const color = teamUser.color || colorPalette.primary.www;
                      return (
                        <Draggable
                          draggableId={teamUser.userId}
                          index={index}
                          key={teamUser.userId}
                          isDragDisabled={state.isRequesting}
                        >
                          {(provided, snapshot) => (
                            <CalendarListItemComponent
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={provided.draggableProps.style}
                            >
                              <CalendarName color={convertHexToRGBA(color, 80)}>
                                <ItemDragIcon name="drag_indicator" />
                                <UserAvatar
                                  src={teamUser.profileImage || ''}
                                  alt={teamUser.name}
                                />
                                <CalendarNameText>
                                  {teamUser.name}
                                </CalendarNameText>
                              </CalendarName>
                              <CloseButtonWrap
                                onClick={() =>
                                  actions.onDeleteUser(teamUser.userId)
                                }
                              >
                                <CloseIcon name="close" />
                              </CloseButtonWrap>
                            </CalendarListItemComponent>
                          )}
                        </Draggable>
                      );
                    })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {props.isLoadingTeamUsers && (
            <>
              <CalendarListItemComponent>
                <Skeleton width="100%" height="44px" />
              </CalendarListItemComponent>
              <CalendarListItemComponent>
                <Skeleton width="100%" height="44px" />
              </CalendarListItemComponent>
              <CalendarListItemComponent>
                <Skeleton width="100%" height="44px" />
              </CalendarListItemComponent>
            </>
          )}
        </ModalContent>
      </MobileFilterContainer>
    </Modal>
  );
});

const MobileFilterContainer = styled.div`
  min-width: 640px;
  max-height: calc(100vh - 100px);
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(12)};
  background-color: ${white};
  overflow-y: auto;
  position: relative;

  ${mediaMax.tablet} {
    min-width: calc(100vw - 24px);
  }
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing(24)} 0;
  margin: 0 ${spacing(24)};
  border-bottom: 1px solid ${gray04};
  position: sticky;
  top: 0;
  background-color: ${white};
  z-index: ${zIndex(1100)};
`;
const ModalCloseIconButton = styled(IconButton)`
  padding: ${spacing(8)};
`;
const ModalContent = styled.div`
  padding: 0 ${spacing(24)} ${spacing(32)};
`;
const InnerModalTitle = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
`;
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 20px;
  margin: ${spacing(16)} 0;
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  color: ${gray03};
`;
const SectionTitle = styled.div`
  flex-grow: 1;
`;
const PlusIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
  color: ${gray02};
`;
const PlusText = styled.div`
  margin-left: ${spacing(4)};
  font-size: ${fontSize(13)};
  color: ${gray02};
  cursor: pointer;
`;
const ProjectContainer = styled.div`
  padding-bottom: ${spacing(12)};
  border-bottom: 1px dashed ${gray04};
  margin-bottom: ${spacing(12)};
`;
const CalendarListItemComponent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  margin-bottom: ${spacing(12)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const ItemDragIcon = styled(Icon)`
  color: ${gray03};
  margin-right: ${spacing(12)};
`;
const CalendarName = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  word-break: break-word;

  .MuiCheckbox-colorPrimary {
    color: ${({ color }) => color};
  }
`;
const CalendarNameText = styled.span`
  /* width: 90px; */
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  ${mediaMax.tablet} {
    width: auto;
  }
`;
const UserAvatar = styled(Avatar)`
  width: 24px;
  height: 24px;
  margin-right: ${spacing(8)};
`;
const NaviIconButton = styled(IconButton)`
  padding: 0;
`;

const CloseIcon = styled(Icon)``;
const CloseButtonWrap = styled(IconButton)`
  padding: ${spacing(4)};
  margin-left: auto;
`;

export default Component;
